import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

export default function VerifyEmailSent() {
  const location = useLocation();
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const email = location.state?.email || '';
  const redirectPath = location.state?.redirectPath;
  const isBlockingVerification = location.state?.isBlockingVerification === true;

  // Announce page load to screen readers
  useEffect(() => {
    // Set focus to the main heading for screen reader navigation
    const heading = document.getElementById('verify-title');
    if (heading) {
      heading.focus();
    }

    // Announce the page content to screen readers
    const announcement = `Email verification required. A link has been sent${email ? ` to ${email}` : ''}. Please verify your email before logging in.`;
    
    // Create a live region announcement
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.setAttribute('class', 'sr-only');
    liveRegion.textContent = announcement;
    document.body.appendChild(liveRegion);

    // Clean up the live region after announcement
    const cleanup = setTimeout(() => {
      if (document.body.contains(liveRegion)) {
        document.body.removeChild(liveRegion);
      }
    }, 3000);

    return () => {
      clearTimeout(cleanup);
      if (document.body.contains(liveRegion)) {
        document.body.removeChild(liveRegion);
      }
    };
  }, [email]);

  const handleResendVerification = async () => {
    if (!email || isResending) {
      return;
    }

    setIsResending(true);
    setResendMessage('');
    setResendError('');

    try {
      const response = await axios.post('/api/auth/resend-verification', {
        email,
        redirectPath
      });
      setResendMessage(response?.data?.message || 'Verification email sent. Please check your inbox.');
    } catch (error) {
      setResendError(error?.response?.data?.message || 'Unable to resend verification email right now. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="auth-container" style={{ background: '#eef2f7' }}>
      {/* Skip to main content link for keyboard navigation */}
      <a 
        href="#verify-title" 
        className="sr-only sr-only-focusable"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: '10px',
          zIndex: 999,
          padding: '8px 16px',
          background: '#000',
          color: '#fff',
          textDecoration: 'none',
          borderRadius: '4px'
        }}
        onFocus={(e) => {
          e.target.style.left = '10px';
          e.target.style.top = '10px';
        }}
        onBlur={(e) => {
          e.target.style.left = '-9999px';
        }}
        onClick={(e) => {
          e.preventDefault();
          console.log('Skip link clicked');
          const titleElement = document.getElementById('verify-title');
          console.log('Title element found:', titleElement);
          if (titleElement) {
            titleElement.focus();
            titleElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            console.log('Title focused and scrolled into view');
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            console.log('Skip link activated via keyboard');
            const titleElement = document.getElementById('verify-title');
            if (titleElement) {
              titleElement.focus();
              titleElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        }}
      >
        Skip to main content
      </a>

      <main 
        className="auth-card register-card"
        style={{
          width: '60vw',
          maxWidth: 760,
          minHeight: 'auto',
          height: 'auto',
          borderRadius: 16,
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          padding: '2rem'
        }}
        role="main"
        aria-labelledby="verify-title"
        aria-describedby="verify-instructions verify-support"
      >
        <h1 
          id="verify-title" 
          className="auth-title" 
          style={{ marginBottom: '0.5rem', color: '#111827' }}
          tabIndex="-1"
        >
          Email Verification Required - ABILITY Job Fair Portal
        </h1>

        <div
          id="verify-instructions"
          role="status"
          aria-live="polite"
          style={{
            background: '#e8f0fe',
            border: '1px solid #c7d2fe',
            color: '#1f2937',
            borderRadius: 10,
            padding: '14px 16px',
            margin: '0 auto 12px',
            lineHeight: 1.6,
            textAlign: 'center'
          }}
        >
          <p style={{ margin: 0 }}>
            A verification link has been sent to your email
            {email && (
              <span>
                {' '}at <span className="email-address" aria-label={`Email address: ${email}`}>{email}</span>
              </span>
            )}.
          </p>
          <p style={{ margin: '8px 0 0 0' }}>
            <strong>{isBlockingVerification ? 'Login is blocked until email verification is complete.' : 'Please verify your email before logging in.'}</strong> If you do not see the email, check your spam or junk folder.
          </p>
        </div>

        <div 
          id="verify-support"
          style={{ textAlign: 'center', marginTop: 16 }}
          role="complementary"
          aria-labelledby="support-heading"
        >
          <h2 
            id="support-heading" 
            className="sr-only"
          >
            Need Help?
          </h2>
          <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#6b7280' }}>
            Need assistance? Contact our support team:
          </p>
          <a
            href="https://abilityjobfair.org/job-seeker-support/"
            target="_blank"
            rel="noopener noreferrer"
            className="terms-link"
            aria-label="Contact Job Seeker Support - Opens in new window"
          >
            Contact Job Seeker Support
            <span className="sr-only"> (opens in new window)</span>
          </a>
        </div>

        <nav 
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', marginTop: '1.5rem' }}
          aria-label="Navigation options"
        >
          <button
            type="button"
            onClick={handleResendVerification}
            className="submit-button"
            style={{ width: 'auto', padding: '0.6rem 1rem' }}
            disabled={!email || isResending}
            aria-live="polite"
            aria-label={email ? 'Resend verification email' : 'Resend verification email unavailable'}
          >
            {isResending ? 'Resending...' : 'Resend Verification Email'}
          </button>
          {resendMessage && (
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#065f46' }} role="status">
              {resendMessage}
            </p>
          )}
          {resendError && (
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#b91c1c' }} role="alert">
              {resendError}
            </p>
          )}
          <Link
            to={redirectPath ? `/login?redirect=${encodeURIComponent(redirectPath)}` : '/login'}
            className="submit-button"
            style={{ width: 'auto', padding: '0.6rem 1rem' }}
            aria-label="Return to login page"
          >
            Back to Login
          </Link>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>
            After verification, return and log in with your email and password.
          </p>
        </nav>

        {/* Hidden live region for screen reader announcements */}
        <div 
          aria-live="polite" 
          aria-atomic="true" 
          className="sr-only"
          id="announcements"
        ></div>
      </main>
    </div>
  );
}
